'use client';

import { cva } from 'class-variance-authority';
import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@/lib/utils/cn';

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root>;
const drawerContentVariants = cva(
  'fixed z-50 flex flex-col bg-modalBg border border-border focus-visible:outline-none',
  {
    variants: {
      position: {
        bottom: 'inset-x-0 bottom-0 rounded-t-[24px]',
        top: 'inset-x-0 top-0 rounded-b-[24px]',
        left: 'inset-y-0 left-0 w-[80%] rounded-r-[24px]',
        right: 'inset-y-0 right-0 w-[80%] rounded-l-[24px]',
      },
    },
    defaultVariants: {
      position: 'bottom',
    },
  }
);
const Drawer = ({
  shouldScaleBackground = true,
  // Avoid restoring the previous page scroll after client navigations that start from an open drawer (iOS Safari).
  preventScrollRestoration = true,
  ...props
}: DrawerProps) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    preventScrollRestoration={preventScrollRestoration}
    {...props}
  />
);

Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerPortal = DrawerPrimitive.Portal;
const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/80 backdrop-blur-sm', className)}
    {...props}
  />
));

DrawerOverlay.displayName = 'DrawerOverlay';

type DrawerContentProps = React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
  position?: 'bottom' | 'top' | 'left' | 'right';
  showHandle?: boolean;
};

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  DrawerContentProps
>(({ className, children, position = 'bottom', showHandle = true, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(drawerContentVariants({ position }), 'max-h-[90vh] px-5 pb-5', className)}
      {...props}
    >
      {showHandle && <div className='mx-auto rounded-full' />}
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
));

DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('w-full border-b border-border py-5 ', className)} {...props} />
);

DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 px-5 py-4', className)} {...props} />
);

DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-md font-bold leading-none tracking-tight', className)}
    {...props}
  />
));

DrawerTitle.displayName = 'DrawerTitle';

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('mt-2 text-sm text-muted-foreground', className)}
    {...props}
  />
));

DrawerDescription.displayName = 'DrawerDescription';

export {
  Drawer,
  DrawerTrigger,
  DrawerPortal,
  DrawerClose,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
